import { OpenInNewOutlineIcon } from '@applyboard/ui-icons';
import { css } from '@emotion/react';
import React, { useContext } from 'react';
import { SrOnly } from '../SrOnly';
import { useFullTheme } from '~/design';
import { IconType } from '~/design/types';
import {
  BackgroundContext,
  LinkCompatibleProps,
  LinkContext,
  LinkContextProps,
  TooltipCompatibleProps,
  useLinkAs,
} from '~/internal';
import { allowedTooltipSymbols } from '~/utils/tooltip';
import { svgVerticalAlign } from '~/utils/styling';

interface CommonProps extends TooltipCompatibleProps {
  /** The aria-label of the link. */
  'aria-label'?: string;

  /** The content of the link. */
  children: LinkCompatibleProps['children'];

  /** The icon that appears on the left side of the link. */
  leadIcon?: IconType;

  /** The saliency of the link. By default, links stand out on a page. Setting saliency=low will
   * change it such that it stands out less. (default='default') */
  saliency?: LinkContextProps['saliency'];

  /** The icon that appears on the right side of the link. */
  trailIcon?: IconType;

  /** The display variation of the link. */
  variant?: 'standalone' | 'inline';
}

interface LinkAsButtonProps extends CommonProps {
  /** If supplied, the component will render with a `button` tag but look like a regular link. */
  as: 'button';

  /** A callback function that is invoked when the link is clicked. */
  onClick: LinkCompatibleProps['onClick'];
}

interface LinkAsLinkProps extends CommonProps {
  as?: 'a';

  /** If specified, the browser will treat the linked URL as a download, not a page. To learn more, see [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#download). */
  download?: string | boolean;

  /** The href of the link (can be relative or absolute). */
  href: LinkCompatibleProps['href'];

  /** Whether or not the link opens in a new tab. */
  mode?: 'override_user_choice_and_open_in_new_tab';

  /** A callback function that is invoked when the link is clicked. In the context of Link this is
   * useful for event tracking. */
  onClick?: LinkCompatibleProps['onClick'];

  /** The rel of the link. For security reasons, it is _highly_ recommended to include 'noopener'
   * in this value. See https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/rel for
   * possible values and formatting. (default: 'noopener') */
  rel?: string;
}

export type LinkProps = LinkAsButtonProps | LinkAsLinkProps;

/**
 * Links allow users to navigate to a different location. They can be presented inline inside a
 * paragraph or as standalone text. They can also be presented as a heading, which can be
 * truncated.
 *
 * A11y docs: https://applyboard.atlassian.net/wiki/spaces/UE/pages/2728788050/Link+Accessibility
 */
export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  {
    'aria-describedby': ariaDescribedby,
    'aria-label': ariaLabel,
    children,
    leadIcon: LeadIcon,
    onBlur,
    onClick,
    onFocus,
    onPointerDown,
    onPointerLeave,
    onPointerMove,
    saliency = 'default',
    trailIcon,
    variant = 'inline',
    ...props
  },
  ref,
) {
  const isButton = props.as === 'button';
  const isNewTabLink = isButton ? false : props.mode === 'override_user_choice_and_open_in_new_tab';
  const rel = isButton ? '' : props.rel ?? 'noopener';

  if (variant === 'inline' && (LeadIcon || trailIcon)) {
    throw new Error(
      'Link component "variant" prop set to "inline" value cannot have lead or trail icons.',
    );
  }
  if (isNewTabLink) {
    if (children && trailIcon) {
      throw new Error('`trailIcon` is not supported in Link when the link opens in a new tab.');
    }
  }
  const { colors, spacing } = useFullTheme();
  const { isBackgroundGradient } = useContext(BackgroundContext);

  const LinkAs = useLinkAs();
  const onAction = !!isBackgroundGradient;
  const lowSaliency = saliency === 'low';

  const visitedStyle = {
    color: onAction ? colors.textOnAction : colors.textMagic,
    ':hover': {
      color: onAction ? colors.textOnAction : colors.textMagicHover,
    },
    ':active': {
      color: onAction ? colors.textOnAction : colors.textMagicPressed,
    },
    ':focus-visible': {
      color: onAction ? colors.textOnAction : colors.textMagic,
    },
  };
  const hoverStyle = {
    color: onAction ? colors.textOnAction : colors.textPrimaryHover,
  };
  const activeStyle = {
    color: onAction ? colors.textOnAction : colors.textPrimaryPressed,
  };

  const linkStyle = css({
    color: onAction
      ? colors.textOnAction
      : lowSaliency
        ? colors.textSecondaryStark
        : colors.textPrimary,
    cursor: 'pointer',
    textDecorationLine: 'underline',
    ':hover': hoverStyle,
    ':active': activeStyle,
    ':focus-visible': {
      borderRadius: spacing[1],
      color: onAction
        ? colors.textOnAction
        : lowSaliency
          ? colors.textSecondaryStark
          : colors.textPrimary,
      outlineColor: onAction ? colors.textOnAction : colors.actionPrimary,
      outlineStyle: 'solid',
      outlineWidth: spacing['2px'],
    },
    ':visited': visitedStyle,
    svg: {
      ':visited': {
        fill: colors.iconMagic,
        ':hover': {
          fill: colors.iconMagicHover,
        },
        ':active': {
          fill: colors.iconMagicPressed,
        },
        ':focus-visible': {
          fill: colors.iconMagic,
        },
      },
    },
    '&:visited > *': {
      color: visitedStyle['color'],
    },
    // This is unfortunately necessary as there seems to be a bug in Chrome + Safari where using
    // "color: inherit" does not apply &:visited and other styles
    '&:hover > *': hoverStyle,
    '&:active > *': activeStyle,
    '&:visited:hover > *': visitedStyle[':hover'],
    '&:visited:active > *': visitedStyle[':active'],
  });

  const standaloneStyle = css({
    alignItems: 'center',
    display: 'flex',
    gap: spacing[2],
    minHeight: spacing[12],
  });

  const TrailIcon = isNewTabLink && !!children ? OpenInNewOutlineIcon : trailIcon;

  if (!isButton && !rel.includes('noopener')) {
    throw new Error(
      '[Link] Link was rendered without including "noopener" in the "rel" attribute. This is a potential security vulnerability.',
    );
  }

  const Tag = isButton ? 'button' : LinkAs;

  return (
    <LinkContext.Provider value={{ saliency }}>
      <Tag
        aria-describedby={ariaDescribedby}
        aria-label={ariaLabel}
        css={[
          isButton && {
            backgroundColor: 'inherit',
            border: 0,
            color: 'unset',
            fontFamily: 'inherit',
            fontSize: 'inherit',
            fontWeight: 'inherit',
            lineHeight: 'inherit',
            letterSpacing: 'inherit',
            padding: 0,
          },
          linkStyle,
          variant === 'standalone' && standaloneStyle,
          svgVerticalAlign,
        ]}
        download={isButton ? undefined : props.download ?? undefined}
        // @ts-expect-error TODO: not sure how to fix this atm
        href={isButton ? undefined : props.href}
        onBlur={onBlur}
        onClick={onClick}
        onFocus={onFocus}
        onPointerDown={onPointerDown}
        onPointerLeave={onPointerLeave}
        onPointerMove={onPointerMove}
        // @ts-expect-error TODO: not sure how to fix this atm
        ref={ref}
        rel={isButton ? undefined : rel}
        target={isNewTabLink ? '_blank' : undefined}
      >
        {variant === 'standalone' && LeadIcon && <LeadIcon size="md" />}
        {children}
        {isNewTabLink && <SrOnly>&nbsp;(Opens in new tab)</SrOnly>}
        {variant === 'standalone' && TrailIcon && (
          <span css={{ flexShrink: 0, flexGrow: 0, height: '24px' }}>
            <TrailIcon size="md" />
          </span>
        )}
      </Tag>
    </LinkContext.Provider>
  );
});

Object.assign(Link, { [allowedTooltipSymbols.Link]: true });
